import { TranscriptComponent } from '../../ConversationTranscript/types';
import { AuditEvent } from './auditData';
import { EVENT_MESSAGE_TYPES } from './buildAudit';
import { parseComponentTimestamp } from './exchanges';

export type MessageItem = {
  component: TranscriptComponent;
  index: number;
  type: 'message';
};

export type EventGroupItem = {
  events: AuditEvent[];
  // Position of this group among the exchange's event groups, used as a stable
  // expand/collapse key alongside the exchange index.
  groupOrdinal: number;
  // Wall-clock time between the message immediately above this group and the
  // message immediately below it — how long the run of events occupied in the
  // conversation. Null when there is no message on one side (a group at the
  // very start or end of the exchange), so nothing is shown there.
  latencyMs: number | null;
  type: 'event-group';
};

export type ExchangeItem = MessageItem | EventGroupItem;

type MessageEntry = {
  at: number;
  kind: 'message';
  item: MessageItem;
};

type EventEntry = {
  at: number;
  kind: 'event';
  event: AuditEvent;
};

// Interleaves an exchange's visible messages with its audit events by wall-clock
// time, then collapses each run of consecutive events into one collapsible group.
// This replaces the rigid "every message, then every event at the foot" layout
// with the conversation's own chronology — a visitor message can be followed by
// two events, the agent's reply, then five more events before the next reply.
export const buildExchangeItems = (
  components: { component: TranscriptComponent; index: number }[],
  events: AuditEvent[],
): ExchangeItem[] => {
  const messages: MessageEntry[] = components
    .filter(
      ({ component }) => !EVENT_MESSAGE_TYPES.includes(component.message_type),
    )
    .map(({ component, index }) => ({
      at: parseComponentTimestamp(component.timestamp),
      item: { component, index, type: 'message' },
      kind: 'message',
    }));

  const eventEntries: EventEntry[] = events.map(event => ({
    at: parseComponentTimestamp(event.timestamp),
    event,
    kind: 'event',
  }));

  // Merge by timestamp. A tie puts an event ahead of a message: the event
  // records a step that produced the message, so it reads first. Array.prototype
  // sort is stable, so equal-timestamp entries of the same kind keep source order.
  const merged = [...messages, ...eventEntries].sort((a, b) => {
    if (a.at !== b.at) {
      return a.at - b.at;
    }
    if (a.kind === b.kind) {
      return 0;
    }
    return a.kind === 'event' ? -1 : 1;
  });

  const items: ExchangeItem[] = [];
  let groupOrdinal = 0;
  let pendingEvents: AuditEvent[] = [];

  const flush = () => {
    if (pendingEvents.length === 0) {
      return;
    }
    items.push({
      events: pendingEvents,
      groupOrdinal,
      latencyMs: null,
      type: 'event-group',
    });
    groupOrdinal += 1;
    pendingEvents = [];
  };

  merged.forEach(entry => {
    if (entry.kind === 'message') {
      flush();
      items.push(entry.item);
      return;
    }
    pendingEvents.push(entry.event);
  });
  flush();

  // Each event group sits between two messages (consecutive events are grouped,
  // so a group's neighbours are always messages when they exist). Its latency is
  // the wall-clock gap between those two messages — the time the run occupied.
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type !== 'event-group') {
      continue;
    }
    const above = items[i - 1];
    const below = items[i + 1];
    if (above?.type !== 'message' || below?.type !== 'message') {
      continue;
    }
    const delta =
      parseComponentTimestamp(below.component.timestamp) -
      parseComponentTimestamp(above.component.timestamp);
    item.latencyMs = Number.isFinite(delta) && delta >= 0 ? delta : null;
  }

  return items;
};
